import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';

const CommentsSection = ({ projectId }) => {
  const { user, isAuthenticated } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');

  useEffect(() => {
    fetchComments();
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchComments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/projects/${projectId}/comments`);
      const data = Array.isArray(response.data) ? response.data : (Array.isArray(response.data?.data) ? response.data.data : []);
      setComments(data);
    } catch (error) {
      console.error('Erreur chargement commentaires:', error);
      toast.error('Erreur lors du chargement des commentaires');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Vous devez être connecté pour commenter');
      return;
    }

    if (!newComment.trim()) {
      toast.error('Veuillez saisir un commentaire');
      return;
    }

    try {
      const response = await axios.post(`/api/projects/${projectId}/comments`, {
        content: newComment.trim()
      });
      
      setComments(prev => [...prev, response.data.comment]);
      setNewComment('');
      toast.success('Commentaire ajouté avec succès !');
    } catch (error) {
      console.error('Erreur ajout commentaire:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de l\'ajout du commentaire');
    }
  };

  const handleReply = async (e, parentId) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Vous devez être connecté pour répondre');
      return;
    }

    if (!replyContent.trim()) {
      toast.error('Veuillez saisir une réponse');
      return;
    }

    try {
      const response = await axios.post(`/api/projects/${projectId}/comments`, {
        content: replyContent.trim(),
        parentId: parentId
      });
      
      setComments(prev => [...prev, response.data.comment]);
      setReplyContent('');
      setReplyingTo(null);
      toast.success('Réponse ajoutée avec succès !');
    } catch (error) {
      console.error('Erreur ajout réponse:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de l\'ajout de la réponse');
    }
  };

  const handleLike = async (commentId) => {
    if (!isAuthenticated) {
      toast.error('Vous devez être connecté pour liker');
      return;
    }

    try {
      const response = await axios.post(`/api/comments/${commentId}/like`);
      setComments(prev => prev.map(comment => 
        comment.id === commentId 
          ? { ...comment, likes: response.data.likes }
          : comment
      ));
    } catch (error) {
      console.error('Erreur like:', error);
      toast.error('Erreur lors du like');
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce commentaire ?')) {
      return;
    }

    try {
      await axios.delete(`/api/comments/${commentId}`);
      setComments(prev => prev.filter(comment => comment.id !== commentId));
      toast.success('Commentaire supprimé avec succès !');
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderComment = (comment, isReply = false) => {
    const isAuthor = user && user.id === comment.author.id;
    const replies = comments.filter(c => c.parentId === comment.id);

    return (
      <div key={comment.id} className={`${isReply ? 'ml-8 mt-4' : 'mb-6'}`}>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                {comment.author.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="font-semibold text-text">{comment.author.name}</div>
                <div className="text-xs text-gray-500 capitalize">{comment.author.role}</div>
              </div>
            </div>
            <div className="text-xs text-gray-500">
              {formatDate(comment.createdAt)}
            </div>
          </div>

          <div className="text-gray-700 mb-3 whitespace-pre-wrap">
            {comment.content}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => handleLike(comment.id)}
                className="flex items-center space-x-1 text-gray-500 hover:text-primary-500 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                <span>{comment.likes || 0}</span>
              </button>

              {!isReply && isAuthenticated && (
                <button
                  onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                  className="text-gray-500 hover:text-primary-500 transition-colors"
                >
                  Répondre
                </button>
              )}
            </div>

            {isAuthor && (
              <button
                onClick={() => handleDelete(comment.id)}
                className="text-red-500 hover:text-red-700 transition-colors text-sm"
              >
                Supprimer
              </button>
            )}
          </div>

          {/* Formulaire de réponse */}
          {replyingTo === comment.id && (
            <form onSubmit={(e) => handleReply(e, comment.id)} className="mt-4 pt-4 border-t border-gray-200">
              <textarea
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder="Écrivez votre réponse..."
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent mb-3"
                required
              />
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setReplyingTo(null);
                    setReplyContent('');
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                >
                  Répondre
                </button>
              </div>
            </form>
          )}

          {/* Réponses */}
          {replies.length > 0 && (
            <div className="mt-4">
              {replies.map(reply => renderComment(reply, true))}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
        <p className="mt-2 text-gray-600">Chargement des commentaires...</p>
      </div>
    );
  }

  return (
    <div className="mt-8">
      <h3 className="text-xl font-semibold text-text mb-6">
        Commentaires ({comments.length})
      </h3>

      {/* Formulaire de nouveau commentaire */}
      {isAuthenticated ? (
        <form onSubmit={handleSubmitComment} className="mb-8">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                {user.firstName.charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="font-semibold text-text">{user.firstName} {user.lastName}</div>
                <div className="text-xs text-gray-500 capitalize">{user.role}</div>
              </div>
            </div>
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Partagez vos pensées sur ce projet..."
              rows={4}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent mb-3"
              required
            />
            <div className="flex justify-end">
              <button
                type="submit"
                className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                Publier
              </button>
            </div>
          </div>
        </form>
      ) : (
        <div className="bg-gray-50 rounded-lg p-6 text-center mb-8">
          <p className="text-gray-600 mb-4">
            Connectez-vous pour laisser un commentaire
          </p>
          <a
            href="/login"
            className="inline-flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Se connecter
          </a>
        </div>
      )}

      {/* Liste des commentaires */}
      {comments.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <p>Aucun commentaire pour le moment</p>
          <p className="text-sm">Soyez le premier à commenter ce projet !</p>
        </div>
      ) : (
        <div className="space-y-4">
          {comments
            .filter(comment => !comment.parentId)
            .map(comment => renderComment(comment))
          }
        </div>
      )}
    </div>
  );
};

export default CommentsSection;

